import {MiddlewareType} from 'middlewares'
import {
  bookOfferCreationConfirm,
  bookOfferCreationError
} from 'modules/book/slice'
import {getRedirectContext} from 'modules/bookingCreation/selectors'
import {getHttpRequestId} from 'modules/common/selectors'
import logBoFHService, {
  BoFHServiceEndpointNames,
  BoFHServiceStatus
} from 'performance/logBoFHService'
import {AnyAction} from 'redux'
import {getCurrentBookingRequestId} from 'utils/bookingRequestId'
import {maskSensitiveData} from 'utils/maskSensitiveData'
import timing from 'utils/timing'

import {getDevicePlatform} from '@vio/core/src/_web/utils/userAgent/device'
import {Status as BookingStatus} from '@vio/core/src/api-types/bovio/response/booking_status'
import {bookApi} from '@vio/core/src/booking/services/bookApi'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'

const requestsTiming = timing()

const middleware: MiddlewareType =
  store => next => async (action: AnyAction) => {
    const {getState} = store
    const state = getState()
    const httpRequestId = getHttpRequestId(state)
    const bookingRequestId = getCurrentBookingRequestId()

    next(action)

    // Booking creation handling

    if (bookApi.endpoints.createBooking.matchPending(action)) {
      const {rooms, customerAccount, payment} =
        action?.meta?.arg?.originalArgs?.payload || {}

      requestsTiming.start('bookOfferCreation')
      const loggedPayload = {
        customerId: customerAccount?.id,
        rooms: rooms.map(room => ({
          guests: room.guests,
          specialRequest: room.specialRequest
        }))
      }

      logBoFHService(
        BoFHServiceEndpointNames.BookingCreation,
        BoFHServiceStatus.start,
        {
          params: loggedPayload,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId,
          platform: getDevicePlatform(),
          paymentMethod: payment?.paymentMethodType
        }
      )
    }

    // Booking finalization handling

    if (bookApi.endpoints.bookingFinalization.matchPending(action)) {
      requestsTiming.start('bookOfferFinalize')

      logBoFHService(
        BoFHServiceEndpointNames.BookingFinalize,
        BoFHServiceStatus.start,
        {
          response: action?.meta?.arg?.originalArgs?.link,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId
        }
      )
    }

    if (bookApi.endpoints.bookingFinalization.matchRejected(action)) {
      const timings = requestsTiming.stop('bookOfferFinalize')

      logBoFHService(
        BoFHServiceEndpointNames.BookingFinalize,
        BoFHServiceStatus.error,
        {
          error: action?.payload,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId,
          timings
        },
        'error'
      )
    }

    // Booking status handling

    if (bookApi.endpoints.getBookingStatus.matchPending(action)) {
      const timings = requestsTiming.start('bookOfferStatus')

      logBoFHService(
        BoFHServiceEndpointNames.BookingStatus,
        BoFHServiceStatus.start,
        {
          link: action?.meta?.arg?.originalArgs?.link,
          timings,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId
        }
      )
    }

    if (bookApi.endpoints.getBookingStatus.matchFulfilled(action)) {
      const timings = requestsTiming.stop('bookOfferStatus')
      const {id, links, status} = action.payload
      const error = action?.payload?.error

      logBoFHService(
        BoFHServiceEndpointNames.BookingStatus,
        BoFHServiceStatus.done,
        {
          link: action?.meta?.arg?.originalArgs?.link,
          response: {id, links, status, error},
          timings,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId
        },
        status === BookingStatus.Error ? 'error' : 'info'
      )

      if (status === BookingStatus.ThreeDomainSecureRequired) {
        const timings = requestsTiming.stop('bookOfferCreation')
        const {id, links, status} = action.payload
        logBoFHService(
          BoFHServiceEndpointNames.BookingCreation,
          BoFHServiceStatus.done,
          {
            data: {
              id,
              links,
              status
            },
            timings,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId,
            platform: getDevicePlatform(),
            paymentMethod: getRedirectContext(state)?.paymentMethodType
          }
        )
      }
    }

    if (bookApi.endpoints.getBookingStatus.matchRejected(action)) {
      const timings = requestsTiming.stop('bookOfferStatus')

      logBoFHService(
        BoFHServiceEndpointNames.BookingStatus,
        BoFHServiceStatus.error,
        {
          error: action?.payload,
          timings,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId
        },
        'error'
      )
    }

    // Booking confirmation handling

    // It's the only way to catch difference between successful status after creation and finalization
    // Additional parameters inside status request not working (because of cache I guess)
    if ((action as AnyAction).type === bookOfferCreationConfirm.type) {
      const {isWithThreeDomainSecure} = action.payload

      if (isWithThreeDomainSecure) {
        const timings = requestsTiming.stop('bookOfferFinalize')
        logBoFHService(
          BoFHServiceEndpointNames.BookingFinalize,
          BoFHServiceStatus.done,
          {
            response: action.payload,
            timings,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId
          }
        )
      }
    }

    // Resend booking confirmation handling

    if (bookApi.endpoints.sendBookingConfirmation.matchPending(action)) {
      requestsTiming.start('sendBookingConfirmation')

      // TODO: make a types revision, because in types this fields are not optional,
      // but in fact seems it's possible be undefined
      const args = action.meta?.arg?.originalArgs

      const params = args
        ? {
            ...args,
            payload: args.payload
              ? maskSensitiveData(args.payload, ['email'])
              : {}
          }
        : {}

      logBoFHService(
        BoFHServiceEndpointNames.SendBookingConfirmation,
        BoFHServiceStatus.start,
        {
          params,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId
        }
      )
    }

    if (bookApi.endpoints.sendBookingConfirmation.matchRejected(action)) {
      requestsTiming.stop('sendBookingConfirmation')

      logBoFHService(
        BoFHServiceEndpointNames.SendBookingConfirmation,
        BoFHServiceStatus.error,
        {
          error: action.error,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId
        },
        'error'
      )
    }

    if (bookApi.endpoints.sendBookingConfirmation.matchFulfilled(action)) {
      requestsTiming.stop('sendBookingConfirmation')

      // TODO: make a types revision, because in types this fields are not optional,
      // but in fact seems it's possible be undefined
      const args = action.meta?.arg?.originalArgs

      const params = args
        ? {
            ...args,
            payload: args.payload
              ? maskSensitiveData(args.payload, ['email'])
              : {}
          }
        : {}

      logBoFHService(
        BoFHServiceEndpointNames.SendBookingConfirmation,
        BoFHServiceStatus.done,
        {
          params,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId
        }
      )
    }

    if (action.type === bookOfferCreationError.type) {
      const timings = requestsTiming.stop('bookOfferCreation')
      logBoFHService(
        BoFHServiceEndpointNames.BookingCreation,
        BoFHServiceStatus.error,
        {
          error: action?.payload,
          timings,
          request_id: httpRequestId,
          booking_request_id: bookingRequestId,
          platform: getDevicePlatform(),
          paymentMethod: getRedirectContext(state)?.paymentMethodType
        },
        'error'
      )
    }

    // Last used billing address handling

    if (
      bookingRetrieveApi.endpoints.getLastUsedBillingAddress.matchPending(
        action
      )
    ) {
      requestsTiming.start('lastUsedBillingAddress')

      logBoFHService(
        BoFHServiceEndpointNames.LastUsedBillingAddress,
        BoFHServiceStatus.start,
        {request_id: httpRequestId}
      )
    }

    if (
      bookingRetrieveApi.endpoints.getLastUsedBillingAddress.matchFulfilled(
        action
      )
    ) {
      const timings = requestsTiming.stop('lastUsedBillingAddress')

      logBoFHService(
        BoFHServiceEndpointNames.LastUsedBillingAddress,
        BoFHServiceStatus.done,
        {request_id: httpRequestId, timings}
      )
    }

    if (
      bookingRetrieveApi.endpoints.getLastUsedBillingAddress.matchRejected(
        action
      )
    ) {
      const timings = requestsTiming.stop('lastUsedBillingAddress')

      logBoFHService(
        BoFHServiceEndpointNames.LastUsedBillingAddress,
        BoFHServiceStatus.error,
        {request_id: httpRequestId, timings}
      )
    }

    if (bookApi.endpoints.getSupportToken.matchRejected(action)) {
      logBoFHService(
        BoFHServiceEndpointNames.SupportToken,
        BoFHServiceStatus.error,
        {
          bookingId: action?.meta?.arg?.originalArgs?.bookingId,
          status: action?.payload?.status,
          request_id: httpRequestId
        },
        'error'
      )
    }
  }

export default middleware
